// Copyright (c) StrangeBee 2023

import { SharingEventList } from '@Organisms/Shares/EntitySharingList/EntitySharingList';
import { TaskController, FilterInput, TaskInput, TaskOutput, UserOutput, SharedOrgOutput } from 'thehive-sdk';

export interface TaskEventList {
    onList: (caseId: string) => Promise<TaskOutput[]>;
    onListAssignableUsers: (taskId: string) => Promise<UserOutput[]>;
    onCreate: (task: TaskInput, caseId: string) => Promise<void>;
    onEdit: (task: Partial<TaskInput>, taskId: string) => Promise<void>;
    markActionAsRequired: (organisation: string, taskId: string) => Promise<void>;
    markActionAsDone: (organisation: string, taskId: string) => Promise<void>;
    shares: SharingEventList;
}

interface TaskEventListParams {
    locale: (label: string) => string;
    controller: TaskController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const computeEventList = ({ controller, locale, onError, onSuccess }: TaskEventListParams): TaskEventList => ({
    onCreate: (task: TaskInput, caseId: string): Promise<void> => {
        return controller
            .create<TaskInput, TaskOutput>(task, caseId)
            .then(() => onSuccess(true, locale('notification.task.create')))
            .catch(onError);
    },
    onList: (caseId: string): Promise<TaskOutput[]> =>
        controller.list<TaskOutput[]>({
            selector: { head: [{ name: 'getCase', idOrName: caseId }, { name: 'tasks' }], name: 'get-all-tasks' },
        }),
    onListAssignableUsers: (taskId: string): Promise<UserOutput[]> =>
        controller.list<UserOutput[]>({
            selector: {
                head: [{ name: 'getTask', idOrName: taskId }, { name: 'assignableUsers' }],
                name: 'get-task-assignable-users',
            },
        }),
    onEdit: (task, taskId) =>
        controller
            .edit<Partial<TaskInput>, void>(task, taskId)
            .then(() => onSuccess?.(false, locale('notification.task.edit')))
            .catch(onError),
    markActionAsRequired: (organisation, taskId): Promise<void> => {
        return controller
            .markActionAsRequired(taskId, organisation)
            .catch(onError)
            .finally(() => onSuccess(false, locale('notification.task.action.required')));
    },
    markActionAsDone: (organisation, taskId): Promise<void> => {
        return controller
            .markActionAsDone(taskId, organisation)
            .catch(onError)
            .finally(() => onSuccess(false, locale('notification.task.action.done')));
    },
    shares: {
        onList: (taskId: string, filters?: FilterInput[]): Promise<SharedOrgOutput[]> =>
            controller.list({
                selector: {
                    head: [{ name: 'getTask', idOrName: taskId }, { name: 'shares' }],
                    initialFilters: filters,
                    name: 'get-task-sharings',
                },
            }),
        onCaseShareList: (caseId: string, filters?: FilterInput[]): Promise<SharedOrgOutput[]> =>
            controller.list({
                selector: {
                    head: [{ name: 'getCase', idOrName: caseId }, { name: 'shares' }],
                    initialFilters: filters,
                    name: 'get-case-sharing',
                },
            }),
        onAdd: (shares, taskId): void => {
            controller
                .addShare(shares as string[], taskId)
                .then(() => onSuccess?.(true, locale('notification.task.sharing.add')))
                .catch(onError);
        },
        onDelete: (organisations, taskId) => {
            controller
                .deleteShare(organisations, taskId)
                .then(() => onSuccess?.(true, locale('notification.task.sharing.remove')))
                .catch(onError);
        },
    },
});
